/* eslint-disable consistent-return */

import {
  handleUpdateStudyPatientContact,
  handleUpdateStudyPatientInformation,
  handleUpdateStudyPatientInsurance,
  handleUpdateStudyPatientMedicalHistory,
  handleUpdateStudySecondaryContact,
} from '@/entities/study';
import { DOUBLE_HYPHEN_BULLET, FREQUENTLY_OF_SYMPTOMS_OPTIONS } from '@/shared/config';
import {
  convertDecimalHeightToFeetInch,
  gettextGender,
  checkToFormatPhoneNumber,
  formatPhoneNumber,
  generateFullAddress,
  generateFullName,
} from '@/shared/lib';
import _ from 'lodash';
import { t } from 'ttag';
import { renderRowItem } from './studyDetailsTab';
import ContactDrawer from '../ui/patientDetailsTab/ContactDrawer';
import InformationDrawer from '../ui/patientDetailsTab/InformationDrawer';
import InsuranceDrawer from '../ui/patientDetailsTab/InsuranceDrawer';
import MedicalDrawer from '../ui/patientDetailsTab/MedicalDrawer';
import SecondDrawer from '../ui/patientDetailsTab/SecondDrawer';
import { PATIENT_DETAIL_KEY } from '../config/patientDetailsTab';

/**
 * Renders the read-only demographic rows of a study's patient details. Builds the name through generateFullName, the translated gender, patient id, dob, height converted to feet and inches, weight to one decimal in pounds, ethnicity, pacemaker and a Yes/No icd flag, defaulting to the double-hyphen bullet, then maps each pair through renderRowItem.
 *
 * @param data - The study, read for info.patient and onboarding.
 * @returns One rendered row per demographic field.
 */
const generatePatientInfo = (data: any) => {
  const { info, onboarding } = data;
  const { patient } = info || {};
  const heightFormat = convertDecimalHeightToFeetInch(patient?.height);
  const arrayItem = [
    {
      label: t`Name`,
      value: generateFullName(patient),
    },
    {
      label: t`Gender`,
      value: gettextGender(patient?.gender) || DOUBLE_HYPHEN_BULLET,
    },
    {
      label: t`ID`,
      value: patient?.patientId || DOUBLE_HYPHEN_BULLET,
    },
    {
      label: t`DOB`,
      value: patient?.dob || DOUBLE_HYPHEN_BULLET,
    },
    {
      label: t`Height`,
      value: !_.isEmpty(heightFormat)
        ? `${heightFormat.ft} ft ${heightFormat.ins} in`
        : DOUBLE_HYPHEN_BULLET,
    },
    {
      label: t`Weight`,
      value: patient?.weight ? `${parseFloat(patient.weight).toFixed(1)} lb` : DOUBLE_HYPHEN_BULLET,
    },
    {
      label: t`Ethnicity`,
      value: patient?.ethnicity || DOUBLE_HYPHEN_BULLET,
    },
    {
      label: t`Pacemaker`,
      value: onboarding?.pacer || DOUBLE_HYPHEN_BULLET,
    },
    {
      label: t`ICD`,
      value: onboarding?.icd ? 'Yes' : 'No',
    },
  ];
  return arrayItem.map((content) => renderRowItem(content));
};

/**
 * Renders the read-only contact rows of a study's patient details. Builds the address through generateFullAddress plus zip, both phone numbers and email, reformatting the phones with formatPhoneNumber only when checkToFormatPhoneNumber accepts the contact country, then maps each pair through renderRowItem.
 *
 * @param data - The study, read for info.patient.contact.
 * @returns One rendered row per contact field.
 */
const generatePatientContact = (data: any) => {
  const { info } = data;
  const { patient } = info || {};
  const { contact } = patient || {};
  const shouldFormatPhoneNumber = checkToFormatPhoneNumber(info?.patient?.contact?.country);
  const arrayItem = [
    {
      label: t`Address`,
      value: generateFullAddress({ contact }),
    },
    {
      label: t`Zip code`,
      value: contact?.zip || DOUBLE_HYPHEN_BULLET,
    },
    {
      label: t`Phone number`,
      value:
        (shouldFormatPhoneNumber ? formatPhoneNumber(contact?.phone1) : contact?.phone1) ||
        DOUBLE_HYPHEN_BULLET,
    },
    {
      label: t`2nd phone number`,
      value:
        (shouldFormatPhoneNumber ? formatPhoneNumber(contact?.phone2) : contact?.phone2) ||
        DOUBLE_HYPHEN_BULLET,
    },
    {
      label: t`Email`,
      value: patient?.email || DOUBLE_HYPHEN_BULLET,
    },
  ];
  return arrayItem.map((content) => renderRowItem(content));
};

/**
 * Renders the read-only secondary-contact rows of a study's patient details. Builds name, phone, relationship and email pairs from patient.secondaryContact, reformatting the phone with formatPhoneNumber only when checkToFormatPhoneNumber accepts the primary contact's country, then maps each pair through renderRowItem.
 *
 * @param data - The study, read for info.patient.secondaryContact.
 * @returns One rendered row per secondary-contact field.
 */
const generateSecondaryContact = (data: any) => {
  const { info } = data;
  const { patient } = info || {};
  const { secondaryContact } = patient || {};
  const shouldFormatPhoneNumber = checkToFormatPhoneNumber(info?.patient?.contact?.country);
  const arrayItem = [
    {
      label: t`Name`,
      value: secondaryContact?.name || DOUBLE_HYPHEN_BULLET,
    },
    {
      label: t`Phone number`,
      value:
        (shouldFormatPhoneNumber
          ? formatPhoneNumber(secondaryContact?.phone)
          : secondaryContact?.phone) || DOUBLE_HYPHEN_BULLET,
    },
    {
      label: t`Relationship`,
      value: secondaryContact?.relationship || DOUBLE_HYPHEN_BULLET,
    },
    {
      label: t`Email`,
      value: secondaryContact?.email || DOUBLE_HYPHEN_BULLET,
    },
  ];
  return arrayItem.map((content) => renderRowItem(content));
};

/**
 * Renders the read-only insurance rows of a study's patient details. Builds insurance type, provider and pre-authorization label/value pairs from info.insurance, showing the double-hyphen bullet when a field is empty and Yes/No for the string pre-auth flag, then maps each pair through renderRowItem.
 *
 * @param data - The study, read for info.insurance.
 * @returns One rendered row per insurance field.
 */
const generateInsurance = (data: any) => {
  const { info } = data;
  const { insurance } = info || {};
  const arrayItem = [
    {
      label: t`Insurance type`,
      value: insurance?.type || DOUBLE_HYPHEN_BULLET,
    },
    {
      label: t`Provider`,
      value: insurance?.provider || DOUBLE_HYPHEN_BULLET,
    },
    {
      label: t`Pre-authorization required`,
      value: insurance?.preAuth === 'true' ? 'Yes' : 'No',
    },
  ];
  return arrayItem.map((content) => renderRowItem(content));
};

/**
 * Renders the read-only medical-history rows of a study's patient details. Builds symptom list, symptom frequency and used-heart-monitor pairs from the onboarding block, printing each symptom on its own line and falling back to the double-hyphen bullet whenever no symptoms are recorded, then maps each pair through renderRowItem.
 *
 * @param data - The study, read for its onboarding block.
 * @returns One rendered row per medical-history field.
 */
const generateMedicalHistory = (data: any) => {
  const { onboarding } = data;
  const arrayItem = [
    {
      label: t`Symptoms the patient experienced`,
      value:
        onboarding?.patientSymptoms && onboarding?.patientSymptoms.length > 0
          ? _.map(onboarding?.patientSymptoms, (item) => (
              <span>
                {item}
                <br />
              </span>
            ))
          : DOUBLE_HYPHEN_BULLET,
    },
    {
      label: t`Symptom frequency`,
      value:
        onboarding?.patientSymptoms && onboarding?.patientSymptoms.length > 0
          ? onboarding?.frequentlyOfSymptoms
          : DOUBLE_HYPHEN_BULLET,
    },
    {
      label: t`Used heart monitor`,
      value:
        onboarding?.patientSymptoms &&
        onboarding?.patientSymptoms.length > 0 &&
        _.isBoolean(onboarding?.usedHeartMonitor)
          ? onboarding?.usedHeartMonitor
            ? 'Yes'
            : 'No'
          : DOUBLE_HYPHEN_BULLET,
    },
  ];
  return arrayItem.map((content) => renderRowItem(content));
};

/**
 * Picks the patient-details drawer matching the section being edited. Switches data.title against the PATIENT_DETAIL_KEY values and spreads data into the information, contact, secondary-contact, insurance or medical-history drawer, returning null for any other title.
 *
 * @param data - The drawer props, whose title selects the section.
 * @returns The matching drawer element, or null.
 */
const generateInputList = (data: any) => {
  switch (data.title) {
    case PATIENT_DETAIL_KEY.PATIENT_INFORMATION: {
      return <InformationDrawer {...data} />;
    }
    case PATIENT_DETAIL_KEY.PATIENT_CONTACT: {
      return <ContactDrawer {...data} />;
    }
    case PATIENT_DETAIL_KEY.SECONDARY_CONTACT: {
      return <SecondDrawer {...data} />;
    }
    case PATIENT_DETAIL_KEY.INSURANCE: {
      return <InsuranceDrawer {...data} />;
    }
    case PATIENT_DETAIL_KEY.MEDICAL_HISTORY: {
      return <MedicalDrawer {...data} />;
    }
    default: {
      return null;
    }
  }
};

/**
 * Detects whether patient identity fields changed enough to warrant re-rendering the report. Walks firstName, lastName, patientId, dob, gender, pacer and icd and returns true at the first key that is present in data and not deep-equal to the same key in compareData.
 *
 * @param data - The current patient values.
 * @param compareData - The previous patient values to compare against.
 * @returns True when a tracked patient field differs.
 */
const checkChangeRerenderReport = (data: any, compareData: any) => {
  const keys = ['firstName', 'lastName', 'patientId', 'dob', 'gender', 'pacer', 'icd'];
  let isChange = false;
  _.forEach(keys, (key) => {
    if (data?.[key] && !_.isEqual(data?.[key], compareData?.[key])) {
      isChange = true;
      return false;
    }
  });
  return isChange;
};

/**
 * Builds the initial form values of the patient information drawer. Reads info.patient and onboarding, splits the decimal height into feet and inches through convertDecimalHeightToFeetInch, and returns the names, patient id, dob, gender, height parts, weight, ethnicity defaulting to null, and the pacer and icd flags.
 *
 * @param data - The study, read for info.patient and onboarding.
 * @returns The patient information form's starting values.
 */
const initialState = (data: any) => {
  const { patient } = data?.info || {};
  const { onboarding } = data;
  const heightFormat = convertDecimalHeightToFeetInch(patient?.height);
  return {
    firstName: patient?.firstName,
    middleName: patient?.middleName,
    lastName: patient?.lastName,
    patientId: patient?.patientId,
    dob: patient?.dob,
    gender: patient?.gender,
    heightFt: heightFormat?.ft,
    heightIn: heightFormat?.ins,
    weight: patient?.weight,
    ethnicity: patient?.ethnicity || null,
    pacer: onboarding?.pacer,
    icd: onboarding?.icd,
  };
};

/**
 * Builds the initial form values of the patient contact drawer. Reads info.patient.contact defensively and returns country, address, city, state, zip and both phone numbers alongside the patient's own email.
 *
 * @param data - The study, read for info.patient.contact.
 * @returns The contact form's starting values.
 */
const initialContact = (data: any) => {
  const { info } = data;
  const { patient } = info || {};
  const { contact } = patient || {};
  return {
    country: contact?.country,
    address: contact?.address,
    city: contact?.city,
    state: contact?.state,
    zip: contact?.zip,
    phone1: contact?.phone1,
    phone2: contact?.phone2,
    email: patient?.email,
  };
};

/**
 * Builds the initial form values of the secondary-contact drawer. Returns the country taken from the patient's own contact plus the secondary contact's name, phone, relationship and email, reading every nesting level defensively.
 *
 * @param data - The study, read for info.patient.secondaryContact.
 * @returns The secondary-contact form's starting values.
 */
const initialSecondContact = (data: any) => {
  const { info } = data;
  const { patient } = info || {};
  const { secondaryContact } = patient || {};
  return {
    country: patient?.contact?.country,
    name: secondaryContact?.name,
    phone: secondaryContact?.phone,
    relationship: secondaryContact?.relationship,
    email: secondaryContact?.email,
  };
};

/**
 * Builds the initial form values of the patient insurance drawer. Reads info.insurance defensively and returns its type, provider and pre-authorization flag.
 *
 * @param data - The study, read for info.insurance.
 * @returns The insurance form's starting values.
 */
const initialInsurance = (data: any) => {
  const { info } = data;
  const { insurance } = info || {};
  return {
    type: insurance?.type,
    provider: insurance?.provider,
    preAuth: insurance?.preAuth,
  };
};

/**
 * Builds the initial form values of the patient medical-history drawer. Reads the onboarding block and returns patientSymptoms defaulting to an empty list, the first recorded symptom frequency falling back to the first FREQUENTLY_OF_SYMPTOMS_OPTIONS value, and usedHeartMonitor defaulting to true when unset.
 *
 * @param data - The study, read for its onboarding block.
 * @returns The medical-history form's starting values.
 */
const initialMedicalState = (data: any) => {
  const { onboarding } = data || {};
  return {
    patientSymptoms: onboarding?.patientSymptoms || [],
    frequentlyOfSymptoms:
      onboarding?.frequentlyOfSymptoms?.[0] || FREQUENTLY_OF_SYMPTOMS_OPTIONS[0].value,
    usedHeartMonitor: !_.isNil(onboarding?.usedHeartMonitor) ? onboarding?.usedHeartMonitor : true,
  };
};

/**
 * Persists one section of a study's patient details. Switches on the PATIENT_DETAIL_KEY, merges studyId with the payload — taking payload.patient for the contact and secondary-contact cases — and awaits the matching update mutation for patient information, contact, secondary contact, insurance or medical history.
 *
 * @param key - The PATIENT_DETAIL_KEY naming the section to save.
 * @param payload - The section's edited values.
 * @param studyId - The study the details belong to.
 * @returns A promise settling once the mutation completes.
 */
const handleUpdatePatientDetails = async ({ key, payload, studyId }: any) => {
  switch (key) {
    case PATIENT_DETAIL_KEY.PATIENT_INFORMATION: {
      const input = {
        studyId,
        ...payload,
      };
      await handleUpdateStudyPatientInformation(input);
      break;
    }
    case PATIENT_DETAIL_KEY.PATIENT_CONTACT: {
      const input = {
        studyId,
        ...(payload?.patient || {}),
      };
      await handleUpdateStudyPatientContact(input);
      break;
    }
    case PATIENT_DETAIL_KEY.SECONDARY_CONTACT: {
      const input = {
        studyId,
        ...(payload?.patient || {}),
      };
      await handleUpdateStudySecondaryContact(input);
      break;
    }
    case PATIENT_DETAIL_KEY.INSURANCE: {
      const input = {
        studyId,
        ...payload,
      };
      await handleUpdateStudyPatientInsurance(input);
      break;
    }
    case PATIENT_DETAIL_KEY.MEDICAL_HISTORY: {
      const input = {
        studyId,
        ...payload,
      };
      await handleUpdateStudyPatientMedicalHistory(input);
      break;
    }
    default:
      break;
  }
};

export {
  generateInputList,
  generateInsurance,
  generateMedicalHistory,
  generatePatientContact,
  generatePatientInfo,
  generateSecondaryContact,
  initialContact,
  initialInsurance,
  initialMedicalState,
  initialSecondContact,
  initialState,
  handleUpdatePatientDetails,
  checkChangeRerenderReport,
};
